import { union, uniq } from "lodash";
import { Component, RefObject, createRef, useContext } from "react";
import { RouteComponentProps, withRouter, Link } from "react-router-dom";

import { CountyProjectContext, ProjectPerspective } from "@app/projects";
import RegionStatusFilter from "@app/projects/network-assessment/RegionStatusFilter";

import { CountyProjectContextProps } from "@app/projects/CountyProjectContext";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { querystringify, parseQuerystring } from "@app/shared/utility";
import { ExportType } from "@app/exports/enumerations";
import { FilterType, ProviderNetworkType } from "@app/projects/enumerators";
import { RegionGranularity } from "@app/region-sets";
import { FlyoutLink } from "@app/shared/Flyouts";
import AccuracyInspectorSelectFlyout from "./AccuracyInspectorSelectFlyout";
import { AccuracyTemplate } from "@app/templates";
import { RouteStateInspectorGroups } from "@app/projects/network-assessment/accuracy/createReportState";
import {
  getProjectPerspectiveDescription,
  getProjectPerspectiveFromFilterString
} from "@app/projects/projectPerspectiveConversions";
import DefaultViewFilter from "@app/shared/DefaultViewFilter";
import useCleanHistory, { CleanHistoryProps } from "@app/shared/useCleanHistory";

interface DetailsFiltersProps extends RouteComponentProps {
  accuracyTemplate: AccuracyTemplate;
  disabled?: boolean;
  exportEndpoint: string;
  showCreateReportsMenu?: boolean;
  showStateOnlyItems?: boolean;
}

interface QueryShape {
  all?: string;
  exceptions?: string;
  gaps?: string;
  changedGaps?: string;
  viewBy?: ProjectPerspective;
}

type CombinedFiltersProps = DetailsFiltersProps & CountyProjectContextProps & CleanHistoryProps;

const DetailsFiltersContainer = (props: DetailsFiltersProps) => {
  const projectContext = useContext(CountyProjectContext);
  const pushOrReplaceLocation = useCleanHistory();
  return <DetailsFilters {...props} {...projectContext} pushOrReplaceLocation={pushOrReplaceLocation} />;
};

class DetailsFilters extends Component<CombinedFiltersProps> {
  public hasUpdated = false; // This property is used to prevent an infinite loop in componentDidUpdate when the user presses the back button after navigating through various filters.
  private readonly createReportRef: RefObject<HTMLButtonElement>;

  constructor(props: CombinedFiltersProps) {
    super(props);
    this.createReportRef = createRef();
  }

  public componentDidUpdate() {
    const query = parseQuerystring(this.props.location.search) as QueryShape;
    if (query.viewBy && this.props.perspective !== query.viewBy) {
      if (this.props.availablePerspectives.includes(query.viewBy)) {
        if (!this.hasUpdated) {
          this.props.setPerspective(query.viewBy, true);
          this.hasUpdated = !this.hasUpdated;
        } else {
          this.hasUpdated = !this.hasUpdated;
        }
      }
    }
  }

  public render() {
    const context = this.props;

    const availableRegionSetStatusTypes =
      context.accuracyResult
        ? context.compareToAccuracyResult
          ? union(context.accuracyResult.availableRegionSetStatusTypes, context.compareToAccuracyResult.availableRegionSetStatusTypes)
          : context.accuracyResult.availableRegionSetStatusTypes
        : [];

    const showRegionStatusSelectors =
      context.perspective === ProjectPerspective.ByCounty ||
      context.perspective === ProjectPerspective.All ||
      context.perspective === ProjectPerspective.ByState;

    const perspectiveDropdownItems = context.availablePerspectives.map((perspective) => ({
      key: perspective,
      name: getProjectPerspectiveDescription(perspective),
    }));

    return (
      <div className="details-filters">
        <span className="">
          {!context.regionFilters.inspection && perspectiveDropdownItems.length && (
            <>
              <NewActionMenu
                buttonText={`View by: ${perspectiveDropdownItems.find((item) => item.key === context.perspective).name}`}
                buttonClassNames="qes-button-secondary"
                loading={context.disabled || context.loadingResults || context.loading}
                testId="details-filters__view-by-dropdown"
                // force a rerender on a perspective change
                key={this.props.perspective}
              >
                {perspectiveDropdownItems.map((item) => {
                  const isSelected = item.key === context.perspective;
                  return (
                    <NewActionMenuItem
                      display={item.name}
                      isSelected={isSelected}
                      key={item.key}
                      onClick={() => this.setPerspective(item.key, true)}
                      testId={`details-filters__view-by-option--${item.key}`}
                    />
                  );
                })}
              </NewActionMenu>
              <DefaultViewFilter filterSuffix={"-accuracy"}
                filterOptions={perspectiveDropdownItems.map(item => item.name)}
                onChange={this.setPerspectiveFromFilter} />
            </>
          )}

          {this.props.showCreateReportsMenu && !context.regionFilters.inspection && perspectiveDropdownItems.length && context.perspective === "BY_INSPECTION" &&
            this.getCreateReportMenu()}
        </span>

        <NewActionMenu
          buttonText={<><i className="fa fa-filter" /> Status Filters </>}
          buttonClassNames="qes-button-secondary"
          alignRight={true}
          testId="details-filters__county-status-dropdown"
          disabled={context.loading || context.loadingResults || !!context.regionFilters.county}
        >
          <RegionStatusFilter
            availableRegionSetStatusTypes={availableRegionSetStatusTypes}
            showRegionStatusSelectors={showRegionStatusSelectors}
          />
        </NewActionMenu>
      </div>
    );
  }

  private setPerspectiveFromFilter = (filter: string, forceRefresh: boolean) => {
    const perspective = getProjectPerspectiveFromFilterString(filter);
    this.setPerspective(perspective, forceRefresh);
  }

  private setPerspective = (perspective: ProjectPerspective, forceRefresh: boolean) => {
    if (forceRefresh) {
      this.updatePerspectiveQueryParam(perspective);
    }
    this.props.setPerspective(perspective, forceRefresh);
  }

  private updatePerspectiveQueryParam = (perspective: ProjectPerspective) => {
    const query = parseQuerystring(this.props.location.search);
    const newQuery = {
      ...query,
      viewBy: perspective,
    };
    const qs = querystringify(newQuery);
    this.props.pushOrReplaceLocation({ search: qs });
  }

  private getCreateReportMenu = () => {
    let specialtyGroupCode: string = null;
    let stateRegionCode: string = null;

    if (this.props.regionFilters) {
      specialtyGroupCode = this.props.regionFilters.specialty;
      stateRegionCode = this.props.regionFilters.state;
    }

    let selectedRegionSetIds = this.props.accuracyResult.aggregateDetails && this.props.accuracyResult.aggregateDetails.length > 0
      ? this.props.countyRegionSetIds.filter(c => this.props.accuracyResult.aggregateDetails.map((r) => r.regionCode).includes(c.state)).map(c => c.regionSetId)
      : uniq(this.props.accuracyResult.details.map(d => d.regionSetId));
    // if no records are selected selected, use the current state region set id as the "default" selection for export properties or if there is no state region set id use all county region set ids
    if (selectedRegionSetIds.length === 0) {
      if (stateRegionCode) {
        selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => c.state === stateRegionCode).map(c => c.regionSetId);
      }
      else
        selectedRegionSetIds = this.props.countyRegionSetIds.map(id => id.regionSetId);
    }

    let selectedSpecialtyGroupCodes: string[];
    if (specialtyGroupCode) {
      selectedSpecialtyGroupCodes = [specialtyGroupCode];
    }
    const selectedAllInspectors: RouteStateInspectorGroups[] = this.props.accuracyTemplate?.criteria?.inspectionGroups.map((ig) => {
      const inspectors = ig.inspections.map((i) => i.name);
      return {
        inspectors,
        name: ig.name,
      }
    })

    const createReportLinkInfo = {
      pathname: this.props.exportEndpoint,
      state: {
        allInspectorsSelected: true,
        availableExportTypes: [ExportType.ActionableProviders, ExportType.AllInspections, ExportType.StatusReport],
        countyFilters: this.props.filters.filter(f => f.filterType == FilterType.County).map(f => f.filterValue),
        exportNetworkProviderCount: this.props.accuracyResult.aggregateDetails?.reduce((acc, ad) => acc + ad.providerCount, 0),
        filters: this.props.filters,
        granularity: RegionGranularity.State,
        networkTypes: [ProviderNetworkType.Network],
        projectResourceId: this.props.project.resource.resourceId,
        projectResourceName: this.props.project.resource.name,
        projectURL: this.props.location.pathname + this.props.location.search,
        regionSetIds: selectedRegionSetIds,
        selectedInspectors: selectedAllInspectors,
        specialtyGroupCodes: selectedSpecialtyGroupCodes,
        stateFilters: this.props.filters.filter(f => f.filterType == FilterType.State).map(f => f.filterValue)
      },
    }

    return (
      <span className="bulk-action-group">
        <NewActionMenu
          buttonClassNames="qes-button-primary"
          containerClassnames="create-report-action-menu-container ml-3"
          buttonText="Create Report"
          testId="create-report-dropdown"
          triggerElementOverride={this.createReportRef}
        >
          <div className="col-md-12 action-menu-item">
            <Link to={createReportLinkInfo}>
              All Inspectors
            </Link>
          </div>
          <FlyoutLink
            classNames="action-menu-item col-12 font-weight-normal"
            content={{
              body: <AccuracyInspectorSelectFlyout exportEndpoint={this.props.exportEndpoint} createReportLinkState={createReportLinkInfo.state} />,
              subTitle: "Choose the inspectors you want as part of your downloadable report.",
              title: "Select Inspectors",
            }}
            size="80"
            testId="select-inspectors-flyout"
            triggerElementOverride={this.createReportRef}
          >
            Select Inspectors
          </FlyoutLink>
        </NewActionMenu>
      </span>
    );
  }

}

const WrappedDetailsFilters = withRouter(DetailsFiltersContainer);

export { WrappedDetailsFilters as DetailsFilters };
